import React, { useState, useMemo } from 'react';
import {
  X,
  User,
  Phone,
  Mail,
  MapPin,
  Smartphone,
  ShieldCheck,
  ShieldAlert,
  FileText,
  Key,
  Check,
  CheckCircle2,
  Printer,
  Sparkles,
  AlertCircle,
  UserCheck,
  Search,
  History,
  RotateCcw,
  Edit3,
  Plus,
  Trash2,
  Package,
  ChevronDown,
  ChevronUp,
  ExternalLink,
  Tag,
  Building2,
  Repeat,
  Layers,
  DollarSign,
  Wallet,
} from 'lucide-react';
import { AppUser, CompanyInfo, DeviceType, ServiceTicket, TicketStatus, ServiceType, ContractedCompany, PaymentMethod, PaymentStatus } from '../types';
import {
  generateNextTicketId,
  calculateTicketTotal,
  getTicketGrandTotal,
  formatDateTR,
  formatDateShortTR,
  formatTL,
  formatPhoneDisplay,
  sanitizePhoneInput,
  validatePhoneDigits,
  STATUS_COLORS,
  STATUS_LABELS,
  SERVICE_TYPE_COLORS,
  SERVICE_TYPE_LABELS,
  SERVICE_TYPE_DESCRIPTIONS,
  getStoredDeviceBrands,
  addStoredDeviceBrand,
  getStoredContractedCompanies,
  saveContractedCompanies,
  generateNextContractCompanyId,
  calculateContractCompanyMetrics,
} from '../utils/storage';
import { defaultDeviceTypes } from '../data/initialData';
import { exportTicketIntakePDF } from '../utils/pdfGenerator';
import { EditCustomerModal, CustomerData } from './EditCustomerModal';
import { PatternLockModal, PatternThumbnail, isPatternPassword } from './PatternLockModal';
import { Grid3X3 } from 'lucide-react';

interface NewTicketModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (ticket: ServiceTicket) => void;
  existingTickets: ServiceTicket[];
  companyInfo: CompanyInfo;
  onPrintIntake: (ticket: ServiceTicket) => void;
  deviceTypes?: string[];
  deviceBrands?: string[];
  onOpenDeviceTypesModal?: () => void;
  onUpdateCustomerInfo?: (
    oldIdentifier: string,
    updated: { name: string; phone: string; email: string; address: string }
  ) => void;
  currentUser?: AppUser | null;
}

interface RegisteredDeviceItem {
  id: string;
  deviceType: DeviceType;
  deviceBrand: string;
  deviceModel: string;
  serialNumber: string;
  customerName: string;
  customerPhone: string;
  customerEmail?: string;
  customerAddress?: string;
  ticketCount: number;
  lastTicketId: string;
  lastStatus: TicketStatus;
  lastDate: string;
  lastIssue?: string;
}

interface RegisteredCustomerItem {
  name: string;
  phone: string;
  email: string;
  address: string;
  ticketCount: number;
  devices: RegisteredDeviceItem[];
}

const normalizePhone = (phone: string): string => {
  if (!phone) return '';
  let digits = phone.replace(/\D/g, '');
  if (digits.startsWith('90') && digits.length === 12) digits = digits.slice(2);
  if (digits.startsWith('0') && digits.length === 11) digits = digits.slice(1);
  return digits;
};

/**
 * Checks if a serial number is non-empty, valid, and not a placeholder like '-' or 'yok'.
 * Empty or '-' serial numbers must NOT match or be treated as the same device.
 */
export const isValidDeviceSerial = (serial?: string | null): boolean => {
  if (!serial) return false;
  const clean = serial.trim().toLowerCase();
  if (!clean) return false;
  if (
    clean === '-' ||
    clean === '--' ||
    clean === '---' ||
    clean === '.' ||
    clean === 'yok' ||
    clean === 'belirtilmedi' ||
    clean === 'bos' ||
    clean === 'boş' ||
    clean === 'none' ||
    clean === 'null' ||
    clean === 'undefined'
  ) {
    return false;
  }
  return true;
};

export const NewTicketModal: React.FC<NewTicketModalProps> = (props) => {
  if (!props.isOpen) return null;
  return <NewTicketModalContent {...props} />;
};

const NewTicketModalContent: React.FC<NewTicketModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
  existingTickets,
  companyInfo,
  onPrintIntake,
  deviceTypes = defaultDeviceTypes,
  deviceBrands,
  onOpenDeviceTypesModal,
  onUpdateCustomerInfo,
  currentUser,
}) => {
  const availableDeviceTypes = deviceTypes.length > 0 ? deviceTypes : defaultDeviceTypes;

  // Customer state
  const [customerName, setCustomerName] = useState('');
  const [customerPhone, setCustomerPhone] = useState('');
  const [customerEmail, setCustomerEmail] = useState('');
  const [customerAddress, setCustomerAddress] = useState('');

  // Customer edit modal states
  const [editingCustomer, setEditingCustomer] = useState<CustomerData | null>(null);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);

  // Device state
  const [deviceType, setDeviceType] = useState<DeviceType>(availableDeviceTypes[0] || 'Telefon');
  const [deviceBrand, setDeviceBrand] = useState('');
  const [showBrandSuggestions, setShowBrandSuggestions] = useState(false);
  const [deviceModel, setDeviceModel] = useState('');
  const [serialNumber, setSerialNumber] = useState('');
  const [isUnderWarranty, setIsUnderWarranty] = useState(false); // Garanti Onay Kutusu
  const [physicalCondition, setPhysicalCondition] = useState('');
  const [includedAccessories, setIncludedAccessories] = useState<string[]>([]);
  const [customAccessory, setCustomAccessory] = useState('');
  const [editingAccessoryIdx, setEditingAccessoryIdx] = useState<number | null>(null);
  const [editingAccessoryText, setEditingAccessoryText] = useState('');
  const [devicePassword, setDevicePassword] = useState('');
  const [isPatternModalOpen, setIsPatternModalOpen] = useState(false);
  const [issueDescription, setIssueDescription] = useState('');

  const [printAfterSave, setPrintAfterSave] = useState(true);
  const [errorMsg, setErrorMsg] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Service Type & Contracted Company States
  const [serviceType, setServiceType] = useState<ServiceType>('Ucretli');
  const [outsideServiceCost, setOutsideServiceCost] = useState<number>(0);
  const [contractedCompanyId, setContractedCompanyId] = useState<string>('');
  const [relatedPreviousTicketId, setRelatedPreviousTicketId] = useState<string>('');
  const [previousTicketSearch, setPreviousTicketSearch] = useState<string>('');
  const [showPrevTicketDropdown, setShowPrevTicketDropdown] = useState<boolean>(false);
  const [isRepeatRepair, setIsRepeatRepair] = useState<boolean>(false);
  const [contractedCompanies, setContractedCompanies] = useState<ContractedCompany[]>(() => getStoredContractedCompanies().filter(c => !c.isDeleted));

  // Paid Service Intake Fee Collection States (Kayıt anında ücret alma)
  const [intakeFeeAmount, setIntakeFeeAmount] = useState<number>(0);
  const [intakePaymentMethod, setIntakePaymentMethod] = useState<PaymentMethod>('Nakit');
  const [intakeFeeType, setIntakeFeeType] = useState<string>('Servis Giriş Ücreti');
  const [intakeFeeNote, setIntakeFeeNote] = useState<string>('');
  
  // Quick inline new company modal
  const [isQuickNewCompanyOpen, setIsQuickNewCompanyOpen] = useState(false);
  const [quickCompanyName, setQuickCompanyName] = useState('');
  const [quickCompanyPhone, setQuickCompanyPhone] = useState('');
  const [quickCompanyFee, setQuickCompanyFee] = useState<number>(50000);
  const [quickCompanyAuth, setQuickCompanyAuth] = useState('');

  // Selected company and metrics
  const selectedContractedCompany = useMemo(() => {
    return contractedCompanies.find(c => c.id === contractedCompanyId) || null;
  }, [contractedCompanies, contractedCompanyId]);

  const selectedCompanyMetrics = useMemo(() => {
    if (!selectedContractedCompany) return null;
    return calculateContractCompanyMetrics(selectedContractedCompany, existingTickets);
  }, [selectedContractedCompany, existingTickets]);

  // Handle choosing a service type
  const handleServiceTypeChange = (type: ServiceType) => {
    setServiceType(type);
    if (type === 'Garantili') {
      setIsUnderWarranty(true);
    } else if (type === 'ServisGarantili') {
      setIsUnderWarranty(false);
      setIsRepeatRepair(true);
    } else if (type === 'Sozlesmeli') {
      setIsUnderWarranty(false);
      // Auto-select first active company if none selected
      if (!contractedCompanyId && contractedCompanies.length > 0) {
        const activeComp = contractedCompanies.find(c => c.isActive) || contractedCompanies[0];
        if (activeComp) {
          handleSelectContractedCompany(activeComp.id);
        }
      }
    }
  };

  // Handle selecting a contracted company
  const handleSelectContractedCompany = (companyId: string) => {
    setContractedCompanyId(companyId);
    const comp = contractedCompanies.find(c => c.id === companyId);
    if (comp) {
      setCustomerName(comp.name + (comp.authorizedPerson ? ` (${comp.authorizedPerson})` : ''));
      setCustomerPhone(comp.phone);
      if (comp.email) setCustomerEmail(comp.email);
      if (comp.address) setCustomerAddress(comp.address);
    }
  };

  // Quick save new contracted company
  const handleQuickSaveCompany = (e: React.FormEvent) => {
    e.preventDefault();
    if (!quickCompanyName.trim() || !quickCompanyPhone.trim()) {
      alert('Lütfen firma adı ve telefonunu giriniz.');
      return;
    }
    const nowISO = new Date().toISOString();
    const newId = generateNextContractCompanyId(contractedCompanies);
    const newCompany: ContractedCompany = {
      id: newId,
      code: `SOZ-${String(contractedCompanies.length + 1).padStart(3, '0')}`,
      name: quickCompanyName.trim(),
      authorizedPerson: quickCompanyAuth.trim() || undefined,
      phone: quickCompanyPhone.trim(),
      monthlyFee: Math.max(0, Number(quickCompanyFee) || 0),
      billingCycle: 'Aylık',
      startDate: nowISO,
      isActive: true,
      notes: 'Hızlı servis kaydı esnasında oluşturuldu.',
      payments: [
        {
          id: 'cp_' + Date.now(),
          amount: Math.max(0, Number(quickCompanyFee) || 0),
          date: nowISO,
          paymentMethod: 'EFT/Havale',
          notes: 'Aylık sözleşme açılış bakiyesi',
          recordedBy: currentUser?.name || 'Sistem',
        },
      ],
      createdAt: nowISO,
      updatedAt: nowISO,
    };
    const allStoredCompanies = getStoredContractedCompanies();
    const updatedAll = [newCompany, ...allStoredCompanies];
    setContractedCompanies(updatedAll.filter(c => !c.isDeleted));
    saveContractedCompanies(updatedAll);
    handleSelectContractedCompany(newId);
    setIsQuickNewCompanyOpen(false);
    setQuickCompanyName('');
    setQuickCompanyPhone('');
    setQuickCompanyAuth('');
  };

  // Previous Tickets matching for "Servis Garantili (İş Takibi)"
  const matchingPreviousTickets = useMemo(() => {
    // İş Takibi listesi yalnız kullanıcı arama kutusuna yazmaya başladığında açılır.
    // Gizli (soft-delete) formlar normal servis akışlarında tekrar görünmez.
    const q = previousTicketSearch.trim().toLowerCase();
    if (!q) return [];

    return existingTickets.filter(t => {
      if (t.isDeleted) return false;
      if (t.id.toLowerCase().includes(q)) return true;
      if (t.customerName.toLowerCase().includes(q)) return true;
      if (normalizePhone(t.customerPhone || '').includes(normalizePhone(q))) return true;
      if (t.deviceBrand.toLowerCase().includes(q)) return true;
      if (t.deviceModel.toLowerCase().includes(q)) return true;
      if (t.serialNumber && t.serialNumber.toLowerCase().includes(q)) return true;
      return false;
    }).slice(0, 10);
  }, [existingTickets, previousTicketSearch]);

  // Handle selecting a previous ticket for repeat repair
  const handleSelectPreviousTicket = (prevTicket: ServiceTicket) => {
    setRelatedPreviousTicketId(prevTicket.id);
    setPreviousTicketSearch(`#${prevTicket.id} - ${prevTicket.deviceBrand} ${prevTicket.deviceModel}`);
    setShowPrevTicketDropdown(false);
    setIsRepeatRepair(true);
    setCustomerName(prevTicket.customerName);
    setCustomerPhone(prevTicket.customerPhone);
    if (prevTicket.customerEmail) setCustomerEmail(prevTicket.customerEmail);
    if (prevTicket.customerAddress) setCustomerAddress(prevTicket.customerAddress);
    setDeviceType(prevTicket.deviceType);
    setDeviceBrand(prevTicket.deviceBrand);
    setDeviceModel(prevTicket.deviceModel);
    if (prevTicket.serialNumber) setSerialNumber(prevTicket.serialNumber);
    if (prevTicket.devicePassword) setDevicePassword(prevTicket.devicePassword);
    if (!issueDescription) {
      setIssueDescription(`[Tekrarlayan Arıza / Servis Garanti İade]: #${prevTicket.id} nolu kayıttaki "${prevTicket.issueDescription}" arızası tekrarladı.`);
    }
  };

  // Registered customer autocomplete states
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [showSerialSuggestions, setShowSerialSuggestions] = useState(false);
  const [selectedCustomerDevices, setSelectedCustomerDevices] = useState<RegisteredDeviceItem[]>([]);

  // Extract unique registered customers and all devices across tickets
  const { registeredCustomers, allRegisteredDevices } = useMemo(() => {
    const map = new Map<string, RegisteredCustomerItem>();
    const allDevs: RegisteredDeviceItem[] = [];

    existingTickets.forEach(t => {
      if (t.isDeleted) return;
      const phoneClean = normalizePhone(t.customerPhone || '');
      const key = phoneClean || t.customerName.trim().toLowerCase();
      if (!key) return;

      if (!map.has(key)) {
        map.set(key, {
          name: t.customerName,
          phone: t.customerPhone,
          email: t.customerEmail || '',
          address: t.customerAddress || '',
          ticketCount: 0,
          devices: [],
        });
      }

      const cust = map.get(key)!;
      cust.ticketCount += 1;
      if (!cust.email && t.customerEmail) cust.email = t.customerEmail;
      if (!cust.address && t.customerAddress) cust.address = t.customerAddress;

      const tBrand = (t.deviceBrand || '').trim();
      const tModel = (t.deviceModel || '').trim();
      const tType = t.deviceType || 'Telefon';
      const tSerialClean = (t.serialNumber || '').trim();
      const tHasValidSerial = isValidDeviceSerial(tSerialClean);

      if (!tBrand && !tModel && !tHasValidSerial) return;

      // Find if device already exists in this customer's device list
      // Rule: If serial number is empty or '-', do NOT treat it as the same device as other devices!
      const devIndex = cust.devices.findIndex(d => {
        const dSerial = (d.serialNumber || '').trim();
        const dHasValidSerial = isValidDeviceSerial(dSerial);

        // 1. Strictly require BOTH to have valid serial numbers and match identically
        if (tHasValidSerial && dHasValidSerial) {
          return tSerialClean.toLowerCase() === dSerial.toLowerCase();
        }

        // 2. If either device has no valid serial (e.g. empty or '-'), they are separate/distinct devices
        return false;
      });

      if (devIndex === -1) {
        const newDev: RegisteredDeviceItem = {
          id: `dev-${cust.devices.length}-${t.id}`,
          deviceType: t.deviceType || 'Telefon',
          deviceBrand: tBrand,
          deviceModel: tModel,
          serialNumber: tHasValidSerial ? tSerialClean : '',
          customerName: cust.name,
          customerPhone: cust.phone,
          customerEmail: cust.email,
          customerAddress: cust.address,
          ticketCount: 1,
          lastTicketId: t.id,
          lastStatus: t.status,
          lastDate: t.createdAt,
          lastIssue: t.issueDescription,
        };
        cust.devices.push(newDev);
      } else {
        const existingDev = cust.devices[devIndex];
        existingDev.ticketCount += 1;
        if (new Date(t.createdAt).getTime() > new Date(existingDev.lastDate).getTime()) {
          existingDev.lastTicketId = t.id;
          existingDev.lastStatus = t.status;
          existingDev.lastDate = t.createdAt;
          existingDev.lastIssue = t.issueDescription;
        }
        if (!existingDev.serialNumber && tHasValidSerial) {
          existingDev.serialNumber = tSerialClean;
        }
      }
    });

    map.forEach(c => {
      c.devices.forEach(d => {
        allDevs.push(d);
      });
    });

    return {
      registeredCustomers: Array.from(map.values()),
      allRegisteredDevices: allDevs,
    };
  }, [existingTickets]);

  const [isPastHistoryOpen, setIsPastHistoryOpen] = useState(false);

  // Past tickets matching STRICTLY the serial number entered in this form
  const pastDeviceTickets = useMemo(() => {
    if (!existingTickets || existingTickets.length === 0) return [];
    
    const currentSerial = (serialNumber || '').trim();
    if (!isValidDeviceSerial(currentSerial)) return [];

    return existingTickets
      .filter(t => {
        if (t.isDeleted) return false;
        const tSerial = (t.serialNumber || '').trim();
        if (!isValidDeviceSerial(tSerial)) return false;
        return currentSerial.toLowerCase() === tSerial.toLowerCase();
      })
      .sort(
        (a, b) =>
          new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
      );
  }, [serialNumber, existingTickets]);

  // Check if a device with this EXACT serial number is currently active in service (not delivered, returned or cancelled)
  const activeTicketInService = useMemo(() => {
    if (!existingTickets || existingTickets.length === 0) return null;

    const currentSerial = (serialNumber || '').trim();
    if (!isValidDeviceSerial(currentSerial)) return null;

    return (
      existingTickets.find(t => {
        if (t.isDeleted) return false;
        const isClosed =
          t.status === 'TeslimEdildi' ||
          t.status === 'IadeEdildi' ||
          t.status === 'Iptal';
        if (isClosed) return false;

        const tSerial = (t.serialNumber || '').trim();
        if (!isValidDeviceSerial(tSerial)) return false;

        return currentSerial.toLowerCase() === tSerial.toLowerCase();
      }) || null
    );
  }, [serialNumber, existingTickets]);

  // Matching customers as user types name or phone
  const matchingCustomers = useMemo(() => {
    if (!customerName.trim()) return [];
    const q = customerName.trim().toLowerCase();
    const qPhoneNorm = normalizePhone(customerName);
    return registeredCustomers.filter(
      c =>
        c.name.toLowerCase().includes(q) ||
        c.phone.includes(q) ||
        (qPhoneNorm && normalizePhone(c.phone).includes(qPhoneNorm))
    );
  }, [customerName, registeredCustomers]);

  // Dynamic list of customer's registered devices (auto-updates even if user types phone/name manually)
  const currentCustomerDevices = useMemo(() => {
    const phoneNorm = normalizePhone(customerPhone);
    const nameClean = customerName.trim().toLowerCase();
    if (!phoneNorm && !nameClean) return selectedCustomerDevices;

    const foundCust = registeredCustomers.find(c => {
      const cPhoneNorm = normalizePhone(c.phone);
      const cNameClean = c.name.trim().toLowerCase();
      if (phoneNorm && cPhoneNorm && phoneNorm === cPhoneNorm) return true;
      if (nameClean && cNameClean && nameClean === cNameClean) return true;
      return false;
    });

    return foundCust && foundCust.devices.length > 0 ? foundCust.devices : selectedCustomerDevices;
  }, [customerPhone, customerName, registeredCustomers, selectedCustomerDevices]);

  // Narrowed matching devices when user types in Serial Number field
  const matchingSerialDevices = useMemo(() => {
    const q = serialNumber.trim().toLowerCase();
    // Only search if user typed at least 2 characters and is a valid query
    if (!q || q.length < 2 || !isValidDeviceSerial(q)) return [];

    // 1. Priority to current customer's devices matching the query
    const custMatches: RegisteredDeviceItem[] = [];
    if (currentCustomerDevices.length > 0) {
      currentCustomerDevices.forEach(d => {
        const s = (d.serialNumber || '').trim();
        if (isValidDeviceSerial(s) && s.toLowerCase().includes(q)) {
          custMatches.push(d);
        }
      });
    }

    // 2. Other registered devices strictly matching serial number
    const otherMatches = allRegisteredDevices.filter(d => {
      const s = (d.serialNumber || '').trim();
      if (!isValidDeviceSerial(s)) return false;
      if (custMatches.some(cm => cm.serialNumber.toLowerCase() === s.toLowerCase())) return false;
      return s.toLowerCase().includes(q);
    });

    // Combine and sort by startsWith first
    const combined = [...custMatches, ...otherMatches].sort((a, b) => {
      const sa = (a.serialNumber || '').toLowerCase();
      const sb = (b.serialNumber || '').toLowerCase();
      const aStarts = sa.startsWith(q);
      const bStarts = sb.startsWith(q);
      if (aStarts && !bStarts) return -1;
      if (!aStarts && bStarts) return 1;
      return 0;
    });

    // Return top 5 narrowed results
    return combined.slice(0, 5);
  }, [serialNumber, currentCustomerDevices, allRegisteredDevices]);

  // Extract all available brands (stored + existing tickets) and compute match list
  const allAvailableBrands = useMemo(() => {
    const brandsSet = new Set<string>(deviceBrands && deviceBrands.length > 0 ? deviceBrands : getStoredDeviceBrands());
    existingTickets.forEach(t => {
      if (t.deviceBrand && t.deviceBrand.trim()) {
        brandsSet.add(t.deviceBrand.trim());
      }
    });
    return Array.from(brandsSet).sort((a, b) => a.localeCompare(b, 'tr'));
  }, [deviceBrands, existingTickets]);

  const matchingBrands = useMemo(() => {
    const q = (deviceBrand || '').trim().toLowerCase();
    if (!q) return allAvailableBrands.slice(0, 10);
    return allAvailableBrands
      .filter(b => b.toLowerCase().includes(q))
      .sort((a, b) => {
        const aStarts = a.toLowerCase().startsWith(q);
        const bStarts = b.toLowerCase().startsWith(q);
        if (aStarts && !bStarts) return -1;
        if (!aStarts && bStarts) return 1;
        return a.localeCompare(b, 'tr');
      })
      .slice(0, 10);
  }, [deviceBrand, allAvailableBrands]);

  const handleSelectCustomer = (cust: RegisteredCustomerItem) => {
    setCustomerName(cust.name);
    setCustomerPhone(cust.phone);
    setCustomerEmail(cust.email);
    setCustomerAddress(cust.address);
    setSelectedCustomerDevices(cust.devices);
    setShowSuggestions(false);
  };

  const handleOpenEditCustomer = (
    cust: RegisteredCustomerItem,
    e: React.MouseEvent
  ) => {
    e.stopPropagation();
    e.preventDefault();
    setEditingCustomer({
      name: cust.name,
      phone: cust.phone,
      email: cust.email,
      address: cust.address,
      originalPhoneOrName: cust.phone || cust.name,
    });
    setIsEditModalOpen(true);
    setShowSuggestions(false);
  };

  const handleSaveEditedCustomer = (
    oldIdentifier: string,
    updated: { name: string; phone: string; email: string; address: string }
  ) => {
    setCustomerName(updated.name);
    setCustomerPhone(updated.phone);
    setCustomerEmail(updated.email);
    setCustomerAddress(updated.address);

    if (onUpdateCustomerInfo) {
      onUpdateCustomerInfo(oldIdentifier, updated);
    }
  };

  const handleSelectPreviousDevice = (dev: RegisteredDeviceItem | {
    deviceType: DeviceType;
    deviceBrand: string;
    deviceModel: string;
    serialNumber: string;
  }) => {
    const isAlreadySelected =
      deviceType.trim().toLowerCase() === dev.deviceType.trim().toLowerCase() &&
      deviceBrand.trim().toLowerCase() === dev.deviceBrand.trim().toLowerCase() &&
      deviceModel.trim().toLowerCase() === dev.deviceModel.trim().toLowerCase() &&
      (!dev.serialNumber || !serialNumber || dev.serialNumber.trim().toLowerCase() === serialNumber.trim().toLowerCase());

    if (isAlreadySelected) {
      handleClearDeviceFields();
    } else {
      setDeviceType(dev.deviceType);
      setDeviceBrand(dev.deviceBrand);
      setDeviceModel(dev.deviceModel);
      setSerialNumber(dev.serialNumber || '');
    }
    setShowSerialSuggestions(false);
  };

  // Check if the currently entered device is a new device for this customer
  const isInputNewDeviceForCustomer = useMemo(() => {
    if (!deviceBrand.trim() || !deviceModel.trim()) return false;
    if (!customerName.trim() && !customerPhone.trim()) return false;
    if (currentCustomerDevices.length === 0) return false;

    const bNorm = deviceBrand.trim().toLowerCase();
    const mNorm = deviceModel.trim().toLowerCase();
    const tNorm = deviceType.trim().toLowerCase();
    const sNorm = serialNumber.trim();
    const hasValidSerial = isValidDeviceSerial(sNorm);

    const match = currentCustomerDevices.find(d => {
      const dsNorm = (d.serialNumber || '').trim();
      const dHasSerial = isValidDeviceSerial(dsNorm);

      if (hasValidSerial && dHasSerial) {
        return sNorm.toLowerCase() === dsNorm.toLowerCase();
      }
      return false;
    });

    return !match;
  }, [deviceBrand, deviceModel, deviceType, serialNumber, customerName, customerPhone, currentCustomerDevices]);

  const handleAddCustomAccessory = (e?: React.KeyboardEvent | React.MouseEvent) => {
    if (e && 'key' in e && e.key !== 'Enter') return;
    if (e) e.preventDefault();
    const trimmed = customAccessory.trim();
    if (!trimmed) return;
    if (!includedAccessories.includes(trimmed)) {
      setIncludedAccessories([...includedAccessories, trimmed]);
    }
    setCustomAccessory('');
  };

  const handleRemoveAccessory = (accToRemove: string) => {
    setIncludedAccessories(includedAccessories.filter(a => a !== accToRemove));
    if (editingAccessoryIdx !== null) {
      setEditingAccessoryIdx(null);
    }
  };

  const handleStartEditAccessory = (idx: number, text: string) => {
    setEditingAccessoryIdx(idx);
    setEditingAccessoryText(text);
  };

  const handleSaveEditAccessory = (idx: number) => {
    const trimmed = editingAccessoryText.trim();
    if (trimmed) {
      const updated = [...includedAccessories];
      updated[idx] = trimmed;
      setIncludedAccessories(updated);
    }
    setEditingAccessoryIdx(null);
    setEditingAccessoryText('');
  };

  const handleClearDeviceFields = () => {
    setDeviceType(defaultDeviceTypes[0] || 'Telefon');
    setDeviceBrand('');
    setDeviceModel('');
    setSerialNumber('');
    setIsUnderWarranty(false);
    setPhysicalCondition('');
    setIncludedAccessories([]);
    setDevicePassword('');
    setIssueDescription('');
    setCustomAccessory('');
    setErrorMsg('');
  };

  const handleCreateTicketObject = (): ServiceTicket | null => {
    setErrorMsg('');

    if (serviceType === 'Sozlesmeli' && !contractedCompanyId) {
      setErrorMsg('Lütfen sözleşmeli kurumsal firmayı seçiniz veya "+ Yeni Sözleşmeli Firma" butonu ile ekleyiniz.');
      return null;
    }
    if (!customerName.trim()) {
      setErrorMsg('Lütfen müşteri ad ve soyadını giriniz.');
      return null;
    }
    if (!customerPhone.trim()) {
      setErrorMsg('Lütfen müşterinin telefon numarasını giriniz.');
      return null;
    }
    if (customerPhone.trim().replace(/\D/g, '').length < 10) {
      setErrorMsg('Lütfen telefon numarasını eksiksiz ve birleşik olarak giriniz (Örn: 05555555555 şeklinde 11 rakam).');
      return null;
    }
    if (!deviceBrand.trim() || !deviceModel.trim()) {
      setErrorMsg('Lütfen cihaz marka ve modelini giriniz.');
      return null;
    }
    if (!serialNumber.trim()) {
      setErrorMsg('Lütfen cihazın Seri Numarası veya IMEI numarasını giriniz (Seri No zorunludur).');
      return null;
    }
    if (activeTicketInService) {
      setErrorMsg(
        `Bu cihaz halihazırda serviste işlem görmektedir (#${activeTicketInService.id} - ${STATUS_LABELS[activeTicketInService.status]}). Cihaz teslim edilmeden yeni bir servis kaydı açılamaz!`
      );
      return null;
    }
    if (!issueDescription.trim()) {
      setErrorMsg('Lütfen arıza detayını ve müşteri şikayetini açıklayınız.');
      return null;
    }

    // Auto-register newly entered brand if not already saved
    if (deviceBrand.trim()) {
      addStoredDeviceBrand(deviceBrand.trim());
    }

    const newTicketId = generateNextTicketId(existingTickets, companyInfo?.name);
    const nowISO = new Date().toISOString();
    const isPaidIntake = serviceType === 'Ucretli' && intakeFeeAmount > 0;
    const initialLabor = 0; // Önden alınan para / giriş ücreti işçilik olarak yazılmaz, sadece ödeme dağılımına işlenir
    const initialPaid = isPaidIntake ? intakeFeeAmount : 0;
    const initialTotal = 0;
    const initialPaymentStatus: PaymentStatus = (serviceType === 'Sozlesmeli' || Boolean(contractedCompanyId))
      ? 'Odendi'
      : serviceType === 'Garantili' && initialTotal <= 0
      ? 'Odendi'
      : isPaidIntake
      ? 'KismiOdendi'
      : 'Bekliyor';

    const paymentsArray: ServiceTicket['payments'] = isPaidIntake
      ? [
          {
            id: `PAY-${Date.now()}`,
            amount: intakeFeeAmount,
            method: intakePaymentMethod,
            date: nowISO,
            note: intakeFeeNote.trim()
              ? `${intakeFeeType} - ${intakeFeeNote.trim()}`
              : `${intakeFeeType} (Kayıt anında tahsil edildi)`,
          },
        ]
      : [];

    const paymentBreakdown = isPaidIntake
      ? {
          cash: intakePaymentMethod === 'Nakit' ? intakeFeeAmount : 0,
          card: intakePaymentMethod === 'Kredi Karti' ? intakeFeeAmount : 0,
          transfer: intakePaymentMethod === 'EFT/Havale' ? intakeFeeAmount : 0,
        }
      : undefined;

    return {
      id: newTicketId,
      createdAt: nowISO,
      updatedAt: nowISO,
      serviceType,
      contractedCompanyId: serviceType === 'Sozlesmeli' ? contractedCompanyId : undefined,
      contractedCompanyName: serviceType === 'Sozlesmeli' ? (selectedContractedCompany?.name || undefined) : undefined,
      relatedPreviousTicketId: serviceType === 'ServisGarantili' ? (relatedPreviousTicketId || undefined) : undefined,
      isRepeatRepair: serviceType === 'ServisGarantili' ? true : isRepeatRepair,
      customerName: customerName.trim(),
      customerPhone: customerPhone.trim(),
      customerEmail: customerEmail.trim() || undefined,
      customerAddress: customerAddress.trim() || undefined,
      deviceType,
      deviceBrand: deviceBrand.trim(),
      deviceModel: deviceModel.trim(),
      serialNumber: serialNumber.trim(),
      isUnderWarranty: serviceType === 'Garantili' ? true : isUnderWarranty,
      physicalCondition: physicalCondition.trim() || 'Gözle görülür hasar yok',
      includedAccessories,
      devicePassword: devicePassword.trim() || undefined,
      issueDescription: issueDescription.trim(),
      status: 'Kaydedildi',
      parts: [],
      subServiceName: undefined,
      subServiceCost: 0,
      outsideServiceCost: serviceType === 'DisServis' ? outsideServiceCost : 0,
      subServiceTrackingNo: undefined,
      subServiceNotes: undefined,
      laborCost: initialLabor,
      vatRate: 20,
      discount: 0,
      roundingAdjustment: 0,
      totalAmount: initialTotal,
      paidAmount: initialPaid,
      paymentMethod: isPaidIntake ? intakePaymentMethod : undefined,
      paymentStatus: initialPaymentStatus,
      paymentDate: isPaidIntake ? nowISO : undefined,
      payments: paymentsArray,
      paymentBreakdown,
      technicianNotes: isPaidIntake
        ? `[Giriş Tahsilatı]: ${intakeFeeAmount} ₺ (${intakePaymentMethod} - ${intakeFeeType}) kayıt anında tahsil edildi.`
        : undefined,
      warrantyDays: 30,
      createdByUser: currentUser?.username || 'admin',
      createdByName: currentUser?.name || currentUser?.username || 'Sistem Yöneticisi',
      lastUpdatedByUser: currentUser?.username || 'admin',
      lastUpdatedByName: currentUser?.name || currentUser?.username || 'Sistem Yöneticisi',
      lastUpdatedAt: nowISO,
    };
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (isSubmitting) return;

    const newTicket = handleCreateTicketObject();
    if (!newTicket) return;

    setIsSubmitting(true);
    try {
      onSubmit(newTicket);

      if (printAfterSave) {
        onPrintIntake(newTicket);
      }

      onClose();
    } catch (err) {
      console.error('Error creating ticket:', err);
      setIsSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto bg-slate-900/70 backdrop-blur-sm">
      <div className="min-h-full flex items-center justify-center p-3 sm:p-4 text-center">
        <div className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl lg:max-w-5xl border border-slate-200 overflow-hidden my-auto text-left transform transition-all">
        {/* Header */}
        <div className="bg-slate-900 text-white px-6 py-4 flex items-center justify-between border-b border-slate-800">
          <div className="flex items-center space-x-3">
            <div className="w-9 h-9 rounded-lg bg-blue-600 flex items-center justify-center">
              <FileText className="w-5 h-5 text-white" />
            </div>
            <div>
              <h2 className="text-lg font-bold text-white leading-tight">
                Yeni Müşteri & Cihaz Kayıt Formu
              </h2>
              <p className="text-xs text-slate-400">
                Cihaz kabul bilgilerini doldurarak kayıt ve teslim çıktısı oluşturun
              </p>
            </div>
          </div>

          <button
            onClick={onClose}
            className="text-slate-400 hover:text-white p-1.5 rounded-lg hover:bg-slate-800 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Form Body */}
        <form onSubmit={handleSubmit} className="p-6 space-y-6 max-h-[80vh] overflow-y-auto">
          {errorMsg && (
            <div className="p-3.5 bg-rose-50 border border-rose-200 rounded-xl text-rose-700 text-sm flex items-center space-x-2">
              <AlertCircle className="w-5 h-5 text-rose-500 shrink-0" />
              <span>{errorMsg}</span>
            </div>
          )}

          {/* ========================================================================= */}
          {/* SECTION 0: SERVICE TYPE & ACCOUNT TYPE SELECTION (LIST SCREEN) */}
          {/* ========================================================================= */}
          <div className="bg-slate-50 p-4 sm:p-5 rounded-2xl border border-slate-200 shadow-xs space-y-3">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-1 border-b pb-2.5 border-slate-200/80">
              <span className="text-xs font-bold text-slate-800 uppercase tracking-wider">
                Servis Türü ve Cari Hesap Tipi Seçimi
              </span>
              <span className="text-[11px] text-slate-500 font-medium">
                {SERVICE_TYPE_DESCRIPTIONS[serviceType]}
              </span>
            </div>

            {/* List Selection Screen */}
            <div className="space-y-2">
              {/* 1. Ücretli */}
              <div
                onClick={() => handleServiceTypeChange('Ucretli')}
                className={`w-full p-3 sm:py-3 sm:px-4 rounded-xl border text-left transition-all cursor-pointer flex items-center justify-between gap-3 ${
                  serviceType === 'Ucretli'
                    ? 'bg-white border-blue-500 ring-2 ring-blue-500/20 shadow-xs'
                    : 'bg-white/70 border-slate-200 hover:border-slate-300 hover:bg-white'
                }`}
              >
                <div className="flex items-center gap-3 min-w-0">
                  <div
                    className={`w-4 h-4 rounded-full border flex items-center justify-center shrink-0 transition-colors ${
                      serviceType === 'Ucretli'
                        ? 'border-blue-600 bg-blue-600'
                        : 'border-slate-300 bg-white'
                    }`}
                  >
                    {serviceType === 'Ucretli' && <div className="w-1.5 h-1.5 rounded-full bg-white" />}
                  </div>
                  <div className="min-w-0">
                    <span className="text-xs sm:text-sm font-bold text-slate-900 block leading-tight">
                      {SERVICE_TYPE_LABELS.Ucretli}
                    </span>
                    <span className="text-[11px] text-slate-500 block truncate">
                      Standart müşteri onarımı ve parça/işçilik ücretlendirmesi
                    </span>
                  </div>
                </div>

                <div className="shrink-0">
                  {serviceType === 'Ucretli' ? (
                    <span className="text-[10px] sm:text-[11px] font-bold px-2 py-0.5 rounded-md bg-blue-100 text-blue-800 border border-blue-200">
                      Seçildi
                    </span>
                  ) : (
                    <span className="text-[10px] text-slate-400 font-medium hidden sm:inline">
                      Seçmek için tıkla
                    </span>
                  )}
                </div>
              </div>

              {/* 2. Garantili */}
              <div
                onClick={() => handleServiceTypeChange('Garantili')}
                className={`w-full p-3 sm:py-3 sm:px-4 rounded-xl border text-left transition-all cursor-pointer flex items-center justify-between gap-3 ${
                  serviceType === 'Garantili'
                    ? 'bg-white border-emerald-500 ring-2 ring-emerald-500/20 shadow-xs'
                    : 'bg-white/70 border-slate-200 hover:border-slate-300 hover:bg-white'
                }`}
              >
                <div className="flex items-center gap-3 min-w-0">
                  <div
                    className={`w-4 h-4 rounded-full border flex items-center justify-center shrink-0 transition-colors ${
                      serviceType === 'Garantili'
                        ? 'border-emerald-600 bg-emerald-600'
                        : 'border-slate-300 bg-white'
                    }`}
                  >
                    {serviceType === 'Garantili' && <div className="w-1.5 h-1.5 rounded-full bg-white" />}
                  </div>
                  <div className="min-w-0">
                    <span className="text-xs sm:text-sm font-bold text-slate-900 block leading-tight">
                      {SERVICE_TYPE_LABELS.Garantili}
                    </span>
                    <span className="text-[11px] text-slate-500 block truncate">
                      Garanti kapsamındaki cihaz onarımı
                    </span>
                  </div>
                </div>

                <div className="shrink-0">
                  {serviceType === 'Garantili' ? (
                    <span className="text-[10px] sm:text-[11px] font-bold px-2 py-0.5 rounded-md bg-emerald-100 text-emerald-800 border border-emerald-200">
                      Seçildi
                    </span>
                  ) : (
                    <span className="text-[10px] text-slate-400 font-medium hidden sm:inline">
                      Seçmek için tıkla
                    </span>
                  )}
                </div>
              </div>

              {/* 3. Servis Garantili (İş Takibi) */}
              <div
                onClick={() => handleServiceTypeChange('ServisGarantili')}
                className={`w-full p-3 sm:py-3 sm:px-4 rounded-xl border text-left transition-all cursor-pointer flex items-center justify-between gap-3 ${
                  serviceType === 'ServisGarantili'
                    ? 'bg-white border-amber-500 ring-2 ring-amber-500/20 shadow-xs'
                    : 'bg-white/70 border-slate-200 hover:border-slate-300 hover:bg-white'
                }`}
              >
                <div className="flex items-center gap-3 min-w-0">
                  <div
                    className={`w-4 h-4 rounded-full border flex items-center justify-center shrink-0 transition-colors ${
                      serviceType === 'ServisGarantili'
                        ? 'border-amber-600 bg-amber-600'
                        : 'border-slate-300 bg-white'
                    }`}
                  >
                    {serviceType === 'ServisGarantili' && <div className="w-1.5 h-1.5 rounded-full bg-white" />}
                  </div>
                  <div className="min-w-0">
                    <span className="text-xs sm:text-sm font-bold text-slate-900 block leading-tight">
                      {SERVICE_TYPE_LABELS.ServisGarantili}
                    </span>
                    <span className="text-[11px] text-slate-500 block truncate">
                      Daha önce servisimizden çıkmış tekrarlayan arıza / servis içi garanti takibi
                    </span>
                  </div>
                </div>

                <div className="shrink-0">
                  {serviceType === 'ServisGarantili' ? (
                    <span className="text-[10px] sm:text-[11px] font-bold px-2 py-0.5 rounded-md bg-amber-100 text-amber-800 border border-amber-200">
                      Seçildi
                    </span>
                  ) : (
                    <span className="text-[10px] text-slate-400 font-medium hidden sm:inline">
                      Seçmek için tıkla
                    </span>
                  )}
                </div>
              </div>

              {/* 4. Sözleşmeli Kurumsal Firma */}
              <div
                onClick={() => handleServiceTypeChange('Sozlesmeli')}
                className={`w-full p-3 sm:py-3 sm:px-4 rounded-xl border text-left transition-all cursor-pointer flex items-center justify-between gap-3 ${
                  serviceType === 'Sozlesmeli'
                    ? 'bg-white border-purple-500 ring-2 ring-purple-500/20 shadow-xs'
                    : 'bg-white/70 border-slate-200 hover:border-slate-300 hover:bg-white'
                }`}
              >
                <div className="flex items-center gap-3 min-w-0">
                  <div
                    className={`w-4 h-4 rounded-full border flex items-center justify-center shrink-0 transition-colors ${
                      serviceType === 'Sozlesmeli'
                        ? 'border-purple-600 bg-purple-600'
                        : 'border-slate-300 bg-white'
                    }`}
                  >
                    {serviceType === 'Sozlesmeli' && <div className="w-1.5 h-1.5 rounded-full bg-white" />}
                  </div>
                  <div className="min-w-0">
                    <span className="text-xs sm:text-sm font-bold text-slate-900 block leading-tight">
                      {SERVICE_TYPE_LABELS.Sozlesmeli}
                    </span>
                    <span className="text-[11px] text-slate-500 block truncate">
                      Aylık sabit sözleşme bakiyesinden mahsup edilir (Harici parçalar ekstra tahsil edilir)
                    </span>
                  </div>
                </div>

                <div className="shrink-0">
                  {serviceType === 'Sozlesmeli' ? (
                    <span className="text-[10px] sm:text-[11px] font-bold px-2 py-0.5 rounded-md bg-purple-100 text-purple-800 border border-purple-200">
                      Seçildi
                    </span>
                  ) : (
                    <span className="text-[10px] text-slate-400 font-medium hidden sm:inline">
                      Seçmek için tıkla
                    </span>
                  )}
                </div>
              </div>

              {/* 5. Dış Servis */}
              <div
                onClick={() => handleServiceTypeChange('DisServis')}
                className={`w-full p-3 sm:py-3 sm:px-4 rounded-xl border text-left transition-all cursor-pointer flex items-center justify-between gap-3 ${
                  serviceType === 'DisServis'
                    ? 'bg-white border-cyan-500 ring-2 ring-cyan-500/20 shadow-xs'
                    : 'bg-white/70 border-slate-200 hover:border-slate-300 hover:bg-white'
                }`}
              >
                <div className="flex items-center gap-3 min-w-0">
                  <div className={`w-4 h-4 rounded-full border flex items-center justify-center shrink-0 ${serviceType === 'DisServis' ? 'border-cyan-600 bg-cyan-600' : 'border-slate-300 bg-white'}`}>
                    {serviceType === 'DisServis' && <div className="w-1.5 h-1.5 rounded-full bg-white" />}
                  </div>
                  <div className="min-w-0">
                    <span className="text-xs sm:text-sm font-bold text-slate-900 block leading-tight">{SERVICE_TYPE_LABELS.DisServis}</span>
                    <span className="text-[11px] text-slate-500 block truncate">Şehir dışında yerinde onarım için gidilen servis kaydı</span>
                  </div>
                </div>
                <div className="shrink-0">
                  {serviceType === 'DisServis' ? (
                    <span className="text-[10px] sm:text-[11px] font-bold px-2 py-0.5 rounded-md bg-cyan-100 text-cyan-800 border border-cyan-200">Seçildi</span>
                  ) : (
                    <span className="text-[10px] text-slate-400 font-medium hidden sm:inline">Seçmek için tıkla</span>
                  )}
                </div>
              </div>
            </div>

            {serviceType === 'DisServis' && (
              <div className="p-4 bg-cyan-50/90 rounded-xl border border-cyan-200 space-y-2 animate-in fade-in slide-in-from-top-1">
                <label className="block text-xs font-bold text-cyan-950">Şehir Dışı Servis Giderleri (₺)</label>
                <p className="text-[11px] text-cyan-800">Yakıt, yemek ve benzeri giderlerin toplamını girin.</p>
                <input
                  type="number"
                  min="0"
                  step="any"
                  value={outsideServiceCost || ''}
                  onChange={event => setOutsideServiceCost(Math.max(0, parseFloat(event.target.value) || 0))}
                  placeholder="0"
                  className="w-full sm:max-w-xs px-3 py-2 text-sm font-bold font-mono text-slate-900 border border-cyan-300 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-cyan-500/20"
                />
              </div>
            )}

            {/* CONDITIONAL SUB-PANEL: ÜCRETLİ SERVİS PEŞİN / GİRİŞ ÜCRETİ TAHSİLATI */}
            {serviceType === 'Ucretli' && (
              <div className="p-4 bg-blue-50/90 rounded-xl border border-blue-200 space-y-3 animate-in fade-in slide-in-from-top-1">
                <div className="flex items-center gap-1.5 border-b border-blue-200/80 pb-2">
                  <Wallet className="w-4 h-4 text-blue-700" />
                  <span className="text-xs font-bold text-blue-950">
                    Servis Kabul Anında Ücret / Ön Ödeme (Kapora) Tahsilatı
                  </span>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-12 gap-3 items-start">
                  {/* Amount input */}
                  <div className="sm:col-span-6 space-y-2">
                    <label className="block text-xs font-bold text-blue-950">
                      Alınan Tutar (₺)
                    </label>
                    <div className="relative">
                      <span className="absolute left-3 top-2 text-sm font-bold text-blue-700">₺</span>
                      <input
                        type="number"
                        min="0"
                        step="any"
                        value={intakeFeeAmount === 0 ? '' : intakeFeeAmount}
                        onChange={e => {
                          const val = e.target.value === '' ? 0 : parseFloat(e.target.value);
                          setIntakeFeeAmount(isNaN(val) ? 0 : Math.max(0, val));
                        }}
                        placeholder="0"
                        className="w-full pl-8 pr-20 py-2 text-sm font-bold font-mono text-slate-900 border border-blue-300 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
                      />
                      {intakeFeeAmount > 0 && (
                        <button
                          type="button"
                          onClick={() => setIntakeFeeAmount(0)}
                          className="absolute right-2 top-1.5 text-slate-400 hover:text-rose-600 text-xs px-2 py-1 rounded bg-slate-100 font-bold transition-colors cursor-pointer"
                          title="Sıfırla"
                        >
                          ✕ Sıfırla
                        </button>
                      )}
                    </div>
                  </div>

                  {/* Payment Method & Type */}
                  <div className="sm:col-span-6 space-y-2.5">
                    {/* Payment Method */}
                    <div>
                      <label className="block text-xs font-bold text-blue-950 mb-1">
                        Ödeme Kanalı / Şekli
                      </label>
                      <div className="grid grid-cols-3 gap-1.5">
                        {(
                          [
                            { id: 'Nakit', label: '💵 Nakit' },
                            { id: 'Kredi Karti', label: '💳 Kredi Kartı' },
                            { id: 'EFT/Havale', label: '🏦 Havale / EFT' },
                          ] as const
                        ).map(pm => (
                          <button
                            key={pm.id}
                            type="button"
                            onClick={() => setIntakePaymentMethod(pm.id)}
                            className={`py-1.5 px-2 text-xs font-bold rounded-lg border text-center transition-all cursor-pointer ${
                              intakePaymentMethod === pm.id
                                ? 'bg-blue-600 text-white border-blue-700 shadow-xs'
                                : 'bg-white text-slate-700 border-blue-200 hover:bg-blue-100/50'
                            }`}
                          >
                            {pm.label}
                          </button>
                        ))}
                      </div>
                    </div>

                    {/* Fee description / type preset */}
                    <div>
                      <label className="block text-xs font-bold text-blue-950 mb-1">
                        Tahsilat Türü / Açıklaması
                      </label>
                      <div className="flex flex-wrap gap-1.5 mb-1.5">
                        {[
                          'Servis Giriş Ücreti',
                          'Arıza Tespit Ücreti',
                          'Ön Ödeme / Kapora',
                          'Peşin Onarım Bedeli',
                        ].map(t => (
                          <button
                            key={t}
                            type="button"
                            onClick={() => setIntakeFeeType(t)}
                            className={`text-[10px] px-2 py-0.5 rounded font-medium transition-all cursor-pointer ${
                              intakeFeeType === t
                                ? 'bg-blue-800 text-white font-bold'
                                : 'bg-white/80 text-blue-900 border border-blue-200 hover:bg-blue-100/60'
                            }`}
                          >
                            {t}
                          </button>
                        ))}
                      </div>
                      <input
                        type="text"
                        value={intakeFeeNote}
                        onChange={e => setIntakeFeeNote(e.target.value)}
                        placeholder="Örn: Müşteriden nakit kapora alındı..."
                        className="w-full px-2.5 py-1.5 text-xs border border-blue-300 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-blue-500/20 text-slate-800"
                      />
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* CONDITIONAL SUB-PANEL: SÖZLEŞMELİ FİRMA SEÇİMİ */}
            {serviceType === 'Sozlesmeli' && (
              <div className="p-4 bg-purple-50/90 rounded-xl border border-purple-200 space-y-3 animate-in fade-in slide-in-from-top-1">
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
                  <label className="text-xs font-bold text-purple-950 flex items-center gap-1.5">
                    <Building2 className="w-4 h-4 text-purple-700" />
                    <span>Sözleşmeli Cari Firma Seçimi <span className="text-rose-500">*</span></span>
                  </label>

                  <button
                    type="button"
                    onClick={() => setIsQuickNewCompanyOpen(true)}
                    className="text-[11px] font-bold text-purple-700 hover:text-purple-900 bg-white border border-purple-300 px-2.5 py-1 rounded-lg hover:bg-purple-100/50 flex items-center gap-1 transition-colors cursor-pointer self-start sm:self-auto shadow-2xs"
                  >
                    <Plus className="w-3.5 h-3.5" />
                    <span>Yeni Sözleşmeli Firma Ekle</span>
                  </button>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-12 gap-3">
                  <div className="sm:col-span-7">
                    <select
                      value={contractedCompanyId}
                      onChange={e => handleSelectContractedCompany(e.target.value)}
                      className="w-full px-3 py-2 text-xs font-bold text-purple-950 border border-purple-300 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-purple-500/20"
                    >
                      <option value="">-- Sözleşmeli Firma Seçiniz --</option>
                      {contractedCompanies.map(c => (
                        <option key={c.id} value={c.id}>
                          {c.name} {c.code ? `(${c.code})` : ''} - Aylık Bakiye: {formatTL(c.monthlyFee)}
                        </option>
                      ))}
                    </select>
                  </div>

                  {selectedCompanyMetrics && (
                    <div className="sm:col-span-5 bg-white p-2.5 rounded-lg border border-purple-200 flex items-center justify-between text-xs">
                      <div>
                        <span className="text-[10px] text-purple-700 font-semibold block">Sözleşme / Kalan Limit</span>
                        <span className="font-mono font-bold text-purple-950">
                          {formatTL(selectedCompanyMetrics.remainingBalance)} / {formatTL(selectedCompanyMetrics.effectiveContractRevenue)}
                        </span>
                      </div>
                      <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${
                        selectedCompanyMetrics.healthStatus === 'healthy'
                          ? 'bg-emerald-100 text-emerald-800'
                          : selectedCompanyMetrics.healthStatus === 'warning'
                          ? 'bg-amber-100 text-amber-800'
                          : 'bg-rose-100 text-rose-800'
                      }`}>
                        {selectedCompanyMetrics.healthStatus === 'healthy' ? '✅ Limit Yeterli' : selectedCompanyMetrics.healthStatus === 'warning' ? '⚠️ Limit Azaldı' : '❌ Aşım'}
                      </span>
                    </div>
                  )}
                </div>

                <div className="text-[11px] text-purple-800/90 leading-relaxed bg-white/80 p-2.5 rounded-lg border border-purple-200/60">
                  💡 <strong>Mahsup Mantığı:</strong> Bu servis kaydında girilen parça ve işçilik bedelleri firmanın tanımlı sözleşme limitinden düşülecek; genel nakit/kart kasa raporlarını kirletmeden sözleşme kâr/zarar hesabına işleyecektir.
                </div>
              </div>
            )}

            {/* CONDITIONAL SUB-PANEL: SERVİS GARANTİLİ (İŞ TAKİBİ) ARAMA */}
            {serviceType === 'ServisGarantili' && (
              <div className="p-4 bg-amber-50/90 rounded-xl border border-amber-200 space-y-3 animate-in fade-in slide-in-from-top-1">
                <div className="flex items-center justify-between">
                  <label className="text-xs font-bold text-amber-950 flex items-center gap-1.5">
                    <Repeat className="w-4 h-4 text-amber-700" />
                    <span>Önceki Servis Kaydını Bağlayın (İş Takibi)</span>
                  </label>
                  {relatedPreviousTicketId && (
                    <span className="text-[10px] font-mono font-bold bg-amber-200 text-amber-900 px-2 py-0.5 rounded-md">
                      Bağlı Kayıt: #{relatedPreviousTicketId}
                    </span>
                  )}
                </div>

                <div className="relative">
                  <Search className="w-4 h-4 text-amber-600 absolute left-3 top-2.5 pointer-events-none" />
                  <input
                    type="text"
                    placeholder="Önceki fiş no (#1001), müşteri adı, telefon veya IMEI ile ara..."
                    value={previousTicketSearch}
                    onFocus={() => setShowPrevTicketDropdown(previousTicketSearch.trim().length > 0)}
                    onChange={e => {
                      const nextValue = e.target.value;
                      setPreviousTicketSearch(nextValue);
                      setShowPrevTicketDropdown(nextValue.trim().length > 0);
                    }}
                    className="w-full pl-9 pr-3 py-2 text-xs border border-amber-300 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-amber-500/20 text-slate-800"
                  />

                  {showPrevTicketDropdown && matchingPreviousTickets.length > 0 && (
                    <div className="absolute left-0 right-0 top-full mt-1 bg-white border border-amber-200 shadow-xl rounded-xl z-30 max-h-52 overflow-y-auto divide-y divide-slate-100">
                      <div className="px-3 py-1.5 bg-amber-100/70 text-[10px] font-bold text-amber-900 flex items-center justify-between">
                        <span>Geçmiş Servis Kayıtları</span>
                        <span>Seçerek Bilgileri Otomatik Doldurun</span>
                      </div>
                      {matchingPreviousTickets.map(pt => (
                        <button
                          key={pt.id}
                          type="button"
                          onClick={() => handleSelectPreviousTicket(pt)}
                          className="w-full text-left p-2.5 hover:bg-amber-50 transition-colors flex items-center justify-between text-xs cursor-pointer"
                        >
                          <div>
                            <div className="font-bold text-slate-900 flex items-center gap-1.5">
                              <span className="font-mono text-amber-800">#{pt.id}</span>
                              <span>-</span>
                              <span>{pt.deviceBrand} {pt.deviceModel}</span>
                            </div>
                            <div className="text-[11px] text-slate-500 mt-0.5">
                              {pt.customerName} • {formatPhoneDisplay(pt.customerPhone)} • {pt.issueDescription}
                            </div>
                          </div>
                          <span className="text-[10px] text-slate-400 shrink-0">{formatDateShortTR(pt.createdAt)}</span>
                        </button>
                      ))}
                    </div>
                  )}
                </div>

                <div className="text-[11px] text-amber-900/90 leading-relaxed bg-white/80 p-2.5 rounded-lg border border-amber-200/60">
                  🔁 <strong>İş Takibi:</strong> Servisimizde daha önce yapılmış aynı arıza geri gelmiş cihaz. Müşteriden mükerrer ücret talep edilmez, harcanan parça ve işçilikler iş takibi maliyeti olarak analiz edilir.
                </div>
              </div>
            )}
          </div>

          {/* SECTION 1: Customer Information */}
          <div className="space-y-3">
            <div className="flex items-center space-x-2 text-slate-800 font-semibold text-sm border-b pb-2 border-slate-100">
              <User className="w-4 h-4 text-blue-600" />
              <span>1. Müşteri İletişim Bilgileri</span>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="relative">
                <label className="block text-xs font-semibold text-slate-700 mb-1">
                  Müşteri Adı Soyadı <span className="text-rose-500">*</span>
                </label>
                <div className="relative">
                  <User className="w-4 h-4 text-slate-400 absolute left-3 top-2.5" />
                  <input
                    type="text"
                    required
                    placeholder="Müşteri Adı Soyadı"
                    value={customerName}
                    onChange={e => {
                      setCustomerName(e.target.value);
                      setShowSuggestions(true);
                    }}
                    onFocus={() => setShowSuggestions(true)}
                    onBlur={() => setTimeout(() => setShowSuggestions(false), 200)}
                    className="w-full pl-9 pr-3 py-2 text-sm border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
                  />
                </div>

                {/* Live Autocomplete Suggestions Dropdown */}
                {showSuggestions && matchingCustomers.length > 0 && (
                  <div className="absolute left-0 right-0 top-full mt-1 bg-white border border-blue-200 shadow-xl rounded-xl z-30 max-h-56 overflow-y-auto divide-y divide-slate-100">
                    <div className="px-3 py-1.5 bg-blue-50/80 text-[11px] font-bold text-blue-900 flex items-center justify-between">
                      <span className="flex items-center space-x-1">
                        <UserCheck className="w-3.5 h-3.5 text-blue-600" />
                        <span>Kayıtlı Müşteri Bulundu ({matchingCustomers.length})</span>
                      </span>
                      <span className="text-[10px] font-normal text-slate-500">Tıklayıp Otomatik Doldurun</span>
                    </div>
                    {matchingCustomers.map((cust, idx) => (
                      <div
                        key={idx}
                        onMouseDown={e => {
                          e.preventDefault();
                          handleSelectCustomer(cust);
                        }}
                        className="w-full text-left px-3.5 py-2 hover:bg-blue-50/80 transition-colors flex items-center justify-between text-xs group cursor-pointer"
                      >
                        <div className="flex-1 pr-2">
                          <div className="font-bold text-slate-900 group-hover:text-blue-700 flex items-center space-x-1.5">
                            <span>{cust.name}</span>
                            <span className="text-[10px] bg-blue-100 text-blue-800 font-semibold px-2 py-0.5 rounded-full">
                              {cust.ticketCount} Servis Kaydı
                            </span>
                          </div>
                          <div className="text-[11px] text-slate-500 font-mono mt-0.5">
                            {formatPhoneDisplay(cust.phone)} {cust.email ? `• ${cust.email}` : ''}
                          </div>
                        </div>

                        <button
                          type="button"
                          onMouseDown={e => handleOpenEditCustomer(cust, e)}
                          title="Müşteri Bilgilerini Düzenle"
                          className="px-2 py-1 bg-amber-50 hover:bg-amber-100 border border-amber-200 text-amber-800 font-bold text-[11px] rounded-md flex items-center space-x-1 transition-colors shrink-0 shadow-sm"
                        >
                          <Edit3 className="w-3 h-3 text-amber-600" />
                          <span>Düzenle</span>
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div>
                <div className="flex items-center justify-between mb-1">
                  <label className="block text-xs font-semibold text-slate-700">
                    Telefon Numarası <span className="text-rose-500">*</span>
                  </label>
                  {customerPhone && (
                    <span className="text-[11px] font-mono font-medium text-blue-600 bg-blue-50 px-1.5 py-0.5 rounded">
                      {formatPhoneDisplay(customerPhone)}
                    </span>
                  )}
                </div>
                <div className="relative">
                  <Phone className="w-4 h-4 text-slate-400 absolute left-3 top-2.5" />
                  <input
                    type="tel"
                    inputMode="numeric"
                    pattern="[0-9]*"
                    maxLength={11}
                    required
                    placeholder="Telefon Numarası"
                    value={customerPhone}
                    onChange={e => setCustomerPhone(sanitizePhoneInput(e.target.value))}
                    className="w-full pl-9 pr-3 py-2 text-sm font-mono border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
                  />
                </div>
              </div>

              <div>
                <label className="block text-xs font-semibold text-slate-700 mb-1">E-Posta Adresi (Opsiyonel)</label>
                <div className="relative">
                  <Mail className="w-4 h-4 text-slate-400 absolute left-3 top-2.5" />
                  <input
                    type="email"
                    placeholder="E-posta adresi"
                    value={customerEmail}
                    onChange={e => setCustomerEmail(e.target.value)}
                    className="w-full pl-9 pr-3 py-2 text-sm border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
                  />
                </div>
              </div>

              <div>
                <label className="block text-xs font-semibold text-slate-700 mb-1">Adres (Opsiyonel)</label>
                <div className="relative">
                  <MapPin className="w-4 h-4 text-slate-400 absolute left-3 top-2.5" />
                  <input
                    type="text"
                    placeholder="İl, İlçe / Mahalle"
                    value={customerAddress}
                    onChange={e => setCustomerAddress(e.target.value)}
                    className="w-full pl-9 pr-3 py-2 text-sm border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* SECTION 2: Device Identification & Warranty Checkbox */}
          <div className="space-y-3 pt-2">
            <div className="flex items-center justify-between border-b pb-2 border-slate-100">
              <div className="flex items-center space-x-2 text-slate-800 font-semibold text-sm">
                <span>2. Cihaz Bilgileri & Garanti Durumu</span>
              </div>

              {isInputNewDeviceForCustomer && (
                <div className="flex items-center space-x-1 text-xs font-semibold text-emerald-700 bg-emerald-50 px-2.5 py-0.5 rounded-full border border-emerald-200 animate-in fade-in">
                  <Sparkles className="w-3.5 h-3.5 text-emerald-600" />
                  <span>Otomatik Müşteri Cihaz Listesine Eklenecek</span>
                </div>
              )}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <div className="flex items-center justify-between mb-1">
                  <label className="block text-xs font-semibold text-slate-700">Cihaz Türü</label>
                  {onOpenDeviceTypesModal && (
                    <button
                      type="button"
                      onClick={onOpenDeviceTypesModal}
                      className="text-[11px] text-blue-600 hover:text-blue-700 font-bold hover:underline flex items-center gap-0.5 cursor-pointer"
                    >
                      <Plus className="w-3 h-3" />
                      <span>Tür Ekle / Sil</span>
                    </button>
                  )}
                </div>
                <select
                  value={deviceType}
                  onChange={e => setDeviceType(e.target.value as DeviceType)}
                  className="w-full px-3 py-2 text-sm border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 bg-white font-medium"
                >
                  {availableDeviceTypes.map(type => (
                    <option key={type} value={type}>
                      {type}
                    </option>
                  ))}
                </select>
              </div>

              <div className="relative">
                <div className="flex items-center justify-between mb-1">
                  <label className="block text-xs font-semibold text-slate-700">
                    Marka <span className="text-rose-500">*</span>
                  </label>
                  {matchingBrands.length > 0 && showBrandSuggestions && (
                    <span className="text-[10px] text-emerald-600 font-bold">
                      {matchingBrands.length} Kayıtlı Marka
                    </span>
                  )}
                </div>
                <input
                  type="text"
                  required
                  placeholder="Marka"
                  value={deviceBrand}
                  onFocus={() => setShowBrandSuggestions(true)}
                  onChange={e => {
                    setDeviceBrand(e.target.value);
                    setShowBrandSuggestions(true);
                  }}
                  className="w-full px-3 py-2 text-sm border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
                />

                {/* Live Brand Autocomplete Suggestions Dropdown */}
                {showBrandSuggestions && matchingBrands.length > 0 && (
                  <div className="absolute left-0 right-0 top-full mt-1 bg-white border border-emerald-200 shadow-xl rounded-lg z-40 max-h-48 overflow-y-auto divide-y divide-slate-100 animate-in fade-in duration-150">
                    <div className="px-2.5 py-1 bg-slate-50 text-[10px] font-bold text-slate-700 flex items-center justify-between sticky top-0 z-10 border-b border-slate-200">
                      <span className="flex items-center space-x-1">
                        <Tag className="w-3 h-3 text-emerald-600" />
                        <span>Kayıtlı Markalar ({matchingBrands.length})</span>
                      </span>
                      <button
                        type="button"
                        onClick={() => setShowBrandSuggestions(false)}
                        className="text-[10px] text-slate-400 hover:text-slate-700 cursor-pointer font-bold px-1"
                      >
                        ✕
                      </button>
                    </div>

                    {matchingBrands.map((brand, idx) => (
                      <div
                        key={idx}
                        onMouseDown={e => {
                          e.preventDefault();
                          setDeviceBrand(brand);
                          setShowBrandSuggestions(false);
                        }}
                        className="w-full text-left px-3 py-2 hover:bg-emerald-50 transition-colors flex items-center justify-between text-xs group cursor-pointer"
                      >
                        <div className="flex items-center space-x-2">
                          <Tag className="w-3.5 h-3.5 text-emerald-500 group-hover:scale-110 transition-transform" />
                          <span className="font-semibold text-slate-800 group-hover:text-emerald-900">
                            {brand}
                          </span>
                        </div>
                        <span className="text-[10px] text-emerald-600 font-bold opacity-0 group-hover:opacity-100 transition-opacity">
                          Seç →
                        </span>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div>
                <label className="block text-xs font-semibold text-slate-700 mb-1">
                  Model <span className="text-rose-500">*</span>
                </label>
                <input
                  type="text"
                  required
                  placeholder="Model"
                  value={deviceModel}
                  onChange={e => setDeviceModel(e.target.value)}
                  className="w-full px-3 py-2 text-sm border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="relative">
                <div className="flex items-center justify-between mb-1">
                  <label className="block text-xs font-semibold text-slate-700">
                    Seri Numarası <span className="text-rose-500">*</span>
                  </label>
                  {matchingSerialDevices.length > 0 && (
                    <span className="text-[10px] text-blue-600 font-bold">
                      {matchingSerialDevices.length} Kayıtlı Cihaz Önerisi
                    </span>
                  )}
                </div>
                <input
                  type="text"
                  required
                  placeholder="Seri Numarası"
                  value={serialNumber}
                  onFocus={() => setShowSerialSuggestions(true)}
                  onChange={e => {
                    setSerialNumber(e.target.value);
                    setShowSerialSuggestions(true);
                  }}
                  className="w-full px-3 py-2 text-sm border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 font-mono"
                />

                {/* Live Narrowed Serial Number Autocomplete Suggestions Dropdown */}
                {showSerialSuggestions && matchingSerialDevices.length > 0 && (
                  <div className="absolute left-0 right-0 top-full mt-1 bg-white border border-blue-200 shadow-xl rounded-lg z-40 max-h-44 overflow-y-auto divide-y divide-slate-100">
                    <div className="px-2.5 py-1 bg-slate-50 text-[10px] font-bold text-slate-700 flex items-center justify-between sticky top-0 z-10 border-b border-slate-200">
                      <span className="flex items-center space-x-1">
                        <Smartphone className="w-3 h-3 text-blue-600" />
                        <span>Eşleşen Seri No ({matchingSerialDevices.length})</span>
                      </span>
                      <button
                        type="button"
                        onClick={() => setShowSerialSuggestions(false)}
                        className="text-[10px] text-slate-400 hover:text-slate-700 cursor-pointer font-bold px-1"
                      >
                        ✕
                      </button>
                    </div>

                    {matchingSerialDevices.map((dev, idx) => (
                      <div
                        key={idx}
                        onMouseDown={e => {
                          e.preventDefault();
                          handleSelectPreviousDevice(dev);
                        }}
                        className="w-full text-left px-2.5 py-1.5 hover:bg-blue-50 transition-colors flex items-center justify-between text-xs group cursor-pointer"
                      >
                        <div className="flex-1 min-w-0 pr-2">
                          <div className="flex items-center space-x-1.5 truncate">
                            <span className="font-mono font-bold text-blue-700 bg-blue-50/80 px-1 py-0.2 rounded border border-blue-200 text-[10px] shrink-0">
                              {dev.serialNumber}
                            </span>
                            <span className="font-semibold text-slate-800 text-xs truncate group-hover:text-blue-900">
                              {dev.deviceBrand} {dev.deviceModel}
                            </span>
                            <span className="text-[10px] text-slate-400 shrink-0">
                              ({dev.deviceType})
                            </span>
                          </div>
                        </div>

                        <div className="shrink-0 text-right">
                          <span className="text-[10px] text-blue-600 font-bold group-hover:underline">
                            Seç →
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div>
                <div className="flex items-center justify-between mb-1">
                  <label className="block text-xs font-semibold text-slate-700">
                    Ekran Şifresi / PIN / Kilit Deseni
                  </label>
                  <button
                    type="button"
                    onClick={() => setIsPatternModalOpen(true)}
                    className="text-[11px] font-bold text-indigo-600 hover:text-indigo-700 flex items-center gap-1 bg-indigo-50 hover:bg-indigo-100 px-2 py-0.5 rounded-md border border-indigo-200 transition-colors cursor-pointer"
                    title="3x3 Nokta üzerinde parmağınız veya fare ile kilit deseni çizin"
                  >
                    <Grid3X3 className="w-3 h-3" />
                    <span>{isPatternPassword(devicePassword) ? 'Deseni Düzenle' : 'Desen Çiz'}</span>
                  </button>
                </div>
                <div className="relative flex items-center gap-2">
                  <div className="relative flex-1">
                    <Key className="w-4 h-4 text-slate-400 absolute left-3 top-2.5" />
                    <input
                      type="text"
                      placeholder="Cihaz şifresi veya desen"
                      value={devicePassword}
                      onChange={e => setDevicePassword(e.target.value)}
                      className="w-full pl-9 pr-3 py-2 text-sm border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
                    />
                  </div>
                  {devicePassword && (
                    <PatternThumbnail
                      patternStr={devicePassword}
                      size={36}
                      className="shrink-0 cursor-pointer hover:ring-2 hover:ring-blue-500 transition-all"
                    />
                  )}
                </div>
              </div>
            </div>

            {/* GARANTİ DURUMU ONAY KUTUSU (WARRANTY CHECKBOX) */}
            <div className="bg-slate-50 border border-slate-200 rounded-xl p-3.5 flex items-center justify-between">
              <div className="flex items-center space-x-3">
                {isUnderWarranty ? (
                  <div className="w-8 h-8 rounded-full bg-emerald-100 flex items-center justify-center text-emerald-600">
                    <ShieldCheck className="w-5 h-5" />
                  </div>
                ) : (
                  <div className="w-8 h-8 rounded-full bg-amber-100 flex items-center justify-center text-amber-600">
                    <ShieldAlert className="w-5 h-5" />
                  </div>
                )}
                <div>
                  <h4 className="text-sm font-bold text-slate-800">
                    Garanti Durumu Onayı
                  </h4>
                  <p className="text-xs text-slate-500">
                    Cihazın resmi veya bayi garantisi devam ediyor mu?
                  </p>
                </div>
              </div>

              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={isUnderWarranty}
                  onChange={e => setIsUnderWarranty(e.target.checked)}
                  className="sr-only peer"
                />
                <div className="w-11 h-6 bg-slate-300 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-slate-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-emerald-600"></div>
                <span className="ml-3 text-xs font-bold text-slate-700">
                  {isUnderWarranty ? 'GARANTİ KAPSAMINDA' : 'GARANTİ DIŞI'}
                </span>
              </label>
            </div>

            {/* ACTIVE TICKET IN SERVICE WARNING BANNER (BLOCKS NEW REGISTRATION) */}
            {activeTicketInService && (
              <div className="bg-gradient-to-br from-rose-50 to-red-50/70 border-2 border-rose-500 rounded-xl p-4 text-rose-900 shadow-md space-y-2.5 animate-in fade-in duration-200">
                <div className="flex items-center space-x-2.5 text-rose-800 font-bold text-sm">
                  <div className="p-1.5 bg-rose-600 text-white rounded-lg shadow-xs">
                    <AlertCircle className="w-5 h-5" />
                  </div>
                  <div>
                    <h4 className="font-extrabold text-sm tracking-wide text-rose-950">
                      CİHAZ HALİHAZIRDA SERVİSTE! (YENİ KAYIT AÇILAMAZ)
                    </h4>
                    <p className="text-xs text-rose-700 font-normal">
                      Bu cihaz için henüz teslim edilmemiş açık bir servis kaydı bulunmaktadır.
                    </p>
                  </div>
                </div>

                <div className="bg-white/90 border border-rose-200 rounded-lg p-3 text-xs text-slate-800 shadow-2xs space-y-1.5">
                  <div className="flex flex-wrap items-center justify-between gap-2 border-b border-rose-100 pb-1.5">
                    <div className="flex items-center space-x-2">
                      <span className="font-mono font-bold text-rose-950 bg-rose-100 px-2 py-0.5 rounded border border-rose-300">
                        #{activeTicketInService.id}
                      </span>
                      <span
                        className={`text-[10px] font-bold px-2 py-0.5 rounded-full border ${STATUS_COLORS[activeTicketInService.status].bg} ${STATUS_COLORS[activeTicketInService.status].text} ${STATUS_COLORS[activeTicketInService.status].border}`}
                      >
                        {STATUS_LABELS[activeTicketInService.status]}
                      </span>
                    </div>
                    <span className="text-[11px] text-slate-500">
                      Kayıt Tarihi: <strong>{formatDateTR(activeTicketInService.createdAt)}</strong>
                    </span>
                  </div>

                  <div className="text-[11px] text-slate-700">
                    <strong className="text-slate-900">Mevcut Şikayet/Arıza: </strong>
                    <span>{activeTicketInService.issueDescription}</span>
                  </div>
                </div>

                <div className="flex items-center space-x-1.5 text-xs text-rose-800 font-semibold bg-rose-100/60 p-2 rounded-lg border border-rose-200">
                  <span>⛔</span>
                  <span>
                    Aynı cihaz aynı anda iki farklı servis kaydında bulunamaz. Yeni kayıt açmak için önce mevcut servis kaydını teslim ediniz veya sonuçlandırınız.
                  </span>
                </div>
              </div>
            )}

            {/* PAST DEVICE HISTORY ACCORDION / CARD IN NEW TICKET MODAL */}
            {pastDeviceTickets.length > 0 && (
              <div className="bg-gradient-to-br from-indigo-50/90 via-blue-50/50 to-white border-2 border-indigo-200/90 rounded-xl p-3.5 space-y-2.5 shadow-sm animate-in fade-in duration-200">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-2">
                    <div className="p-1.5 bg-indigo-600 text-white rounded-lg shadow-xs">
                      <History className="w-4 h-4" />
                    </div>
                    <div>
                      <h4 className="text-xs font-bold text-indigo-950 flex items-center gap-1.5">
                        <span>Bu Cihazın Önceki Servis Kayıtları Bulundu!</span>
                        <span className="bg-indigo-600 text-white text-[10px] px-1.5 py-0.2 rounded-full font-bold">
                          {pastDeviceTickets.length} Kayıt
                        </span>
                      </h4>
                      <p className="text-[11px] text-indigo-700">
                        Bu seri numarasına ait daha önceki servis ve arıza geçmişi aşağıda listelenmiştir.
                      </p>
                    </div>
                  </div>

                  <button
                    type="button"
                    onClick={() => setIsPastHistoryOpen(!isPastHistoryOpen)}
                    className="text-indigo-600 hover:text-indigo-800 text-xs font-bold flex items-center space-x-1 p-1 rounded hover:bg-indigo-100/60 transition-colors cursor-pointer"
                  >
                    <span>{isPastHistoryOpen ? 'Gizle' : 'Göster'}</span>
                    {isPastHistoryOpen ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                  </button>
                </div>

                {isPastHistoryOpen && (
                  <div className="space-y-2 pt-1 max-h-48 overflow-y-auto pr-1">
                    {pastDeviceTickets.map(pTicket => {
                      const pTotal = getTicketGrandTotal(pTicket);
                      const pPaid =
                        pTicket.paymentStatus === 'Odendi'
                          ? (pTicket.paidAmount !== undefined && pTicket.paidAmount > 0 ? pTicket.paidAmount : pTotal)
                          : pTicket.paymentStatus === 'KismiOdendi'
                          ? pTicket.paidAmount || 0
                          : 0;
                      const pDebt = Math.max(0, pTotal - pPaid);
                      const isWarrantyValid =
                        pTicket.warrantyDays &&
                        pTicket.warrantyDays > 0 &&
                        pTicket.deliveryDate
                          ? new Date(pTicket.deliveryDate).getTime() +
                              pTicket.warrantyDays * 86400000 >
                            Date.now()
                          : false;

                      return (
                        <div
                          key={pTicket.id}
                          className="bg-white border border-indigo-100 rounded-lg p-2.5 shadow-2xs space-y-1.5 text-xs text-slate-700"
                        >
                          <div className="flex flex-wrap items-center justify-between gap-1.5 border-b border-slate-100 pb-1.5">
                            <div className="flex items-center space-x-2">
                              <span className="font-mono font-bold text-indigo-950 bg-indigo-50 px-1.5 py-0.5 rounded border border-indigo-200">
                                #{pTicket.id}
                              </span>
                              <span
                                className={`text-[10px] font-bold px-2 py-0.5 rounded-full border ${STATUS_COLORS[pTicket.status].bg} ${STATUS_COLORS[pTicket.status].text} ${STATUS_COLORS[pTicket.status].border}`}
                              >
                                {STATUS_LABELS[pTicket.status]}
                              </span>
                            </div>
                            <span className="text-[11px] text-slate-500">
                              Kayıt: <strong>{formatDateTR(pTicket.createdAt)}</strong>
                              {pTicket.deliveryDate && ` • Teslim: ${formatDateTR(pTicket.deliveryDate)}`}
                            </span>
                          </div>

                          <div className="space-y-1 text-[11px]">
                            <div>
                              <strong className="text-slate-900">Arıza / Şikayet: </strong>
                              <span>{pTicket.issueDescription}</span>
                            </div>
                            {pTicket.technicianNotes && (
                              <div className="bg-slate-50 p-1 rounded border border-slate-100 text-slate-600">
                                <strong className="text-slate-800">Yapılan İşlem: </strong>
                                <span>{pTicket.technicianNotes}</span>
                              </div>
                            )}
                            {pTicket.parts && pTicket.parts.length > 0 && (
                              <div>
                                <strong className="text-slate-800">Değişen Parçalar: </strong>
                                <span>{pTicket.parts.map(p => `${p.name} (${p.quantity}x)`).join(', ')}</span>
                              </div>
                            )}
                          </div>

                          <div className="flex items-center justify-between pt-1 border-t border-slate-100 text-[11px] font-medium">
                            <div>
                              {pDebt > 0 ? (
                                <span className="font-bold text-rose-700 bg-rose-50 border border-rose-200 px-2 py-0.5 rounded text-[11px]">
                                  Borç: {formatTL(pDebt)}
                                </span>
                              ) : (
                                <span className="font-medium text-emerald-700 bg-emerald-50 border border-emerald-200 px-2 py-0.5 rounded text-[11px]">
                                  Borç Yok
                                </span>
                              )}
                            </div>
                            {pTicket.warrantyDays !== undefined && pTicket.warrantyDays > 0 && (
                              <span
                                className={`px-1.5 py-0.5 rounded font-bold ${
                                  isWarrantyValid
                                    ? 'bg-emerald-100 text-emerald-800 border border-emerald-300'
                                    : 'bg-slate-100 text-slate-600'
                                }`}
                              >
                                {isWarrantyValid
                                  ? `✓ ${pTicket.warrantyDays} Gün Garanti (Aktif)`
                                  : `${pTicket.warrantyDays} Gün Garanti`}
                              </span>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* SECTION 3: Detailed Device Condition & Accessories */}
          <div className="space-y-3 pt-2">
            <div className="flex items-center space-x-2 text-slate-800 font-semibold text-sm border-b pb-2 border-slate-100">
              <Sparkles className="w-4 h-4 text-blue-600" />
              <span>3. Cihaz Detaylı Durumu & Teslim Alınan Aksesuarlar</span>
            </div>

            <div>
              <label className="block text-xs font-semibold text-slate-700 mb-1">
                Fiziksel Durum Notları (Çizik, Kırık, Darbe, Sıvı Teması vb.)
              </label>
              <textarea
                rows={2}
                placeholder="Fiziksel durum notları..."
                value={physicalCondition}
                onChange={e => setPhysicalCondition(e.target.value)}
                className="w-full px-3 py-2 text-sm border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
              />
            </div>

            <div>
              <div className="flex items-center justify-between mb-1.5">
                <label className="block text-xs font-semibold text-slate-700">
                  Teslim Alınan Aksesuarlar
                </label>
                {includedAccessories.length > 0 && (
                  <button
                    type="button"
                    onClick={() => setIncludedAccessories([])}
                    className="text-[11px] text-rose-600 hover:text-rose-700 font-medium transition-colors cursor-pointer"
                  >
                    Tümünü Temizle
                  </button>
                )}
              </div>

              {/* Aksesuar Ekleme Girişi */}
              <div className="flex gap-2 mb-3">
                <input
                  type="text"
                  placeholder="Aksesuar..."
                  value={customAccessory}
                  onChange={e => setCustomAccessory(e.target.value)}
                  onKeyDown={handleAddCustomAccessory}
                  className="flex-1 px-3 py-2 text-xs border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 bg-white"
                />
                <button
                  type="button"
                  onClick={() => handleAddCustomAccessory()}
                  disabled={!customAccessory.trim()}
                  className="px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:bg-slate-200 disabled:text-slate-400 text-white text-xs font-bold rounded-lg transition-colors flex items-center space-x-1.5 shrink-0 cursor-pointer disabled:cursor-not-allowed shadow-sm"
                >
                  <Plus className="w-4 h-4" />
                  <span>Ekle</span>
                </button>
              </div>

              {/* Teslim Alınacak Aksesuarlar Listesi (Düzeltilebilir ve Silinebilir) */}
              <div className="bg-slate-50 border border-slate-200 rounded-xl p-3">
                <div className="flex items-center justify-between text-xs font-bold text-slate-700 mb-2">
                  <span className="flex items-center space-x-1.5">
                    <Package className="w-4 h-4 text-blue-600" />
                    <span>Teslim Alınacak Aksesuar Listesi ({includedAccessories.length}):</span>
                  </span>
                </div>

                {includedAccessories.length > 0 ? (
                  <div className="flex flex-wrap gap-2">
                    {includedAccessories.map((acc, idx) => {
                      const isEditing = editingAccessoryIdx === idx;
                      if (isEditing) {
                        return (
                          <div key={idx} className="flex items-center bg-white border-2 border-blue-500 rounded-lg p-1 shadow-md">
                            <input
                              type="text"
                              autoFocus
                              value={editingAccessoryText}
                              onChange={e => setEditingAccessoryText(e.target.value)}
                              onKeyDown={e => {
                                if (e.key === 'Enter') {
                                  e.preventDefault();
                                  handleSaveEditAccessory(idx);
                                } else if (e.key === 'Escape') {
                                  setEditingAccessoryIdx(null);
                                }
                              }}
                              className="text-xs px-2 py-0.5 border-0 focus:outline-none w-40 font-bold text-slate-900"
                            />
                            <button
                              type="button"
                              onClick={() => handleSaveEditAccessory(idx)}
                              className="p-1 text-emerald-600 hover:bg-emerald-50 rounded cursor-pointer"
                              title="Kaydet"
                            >
                              <Check className="w-4 h-4" />
                            </button>
                            <button
                              type="button"
                              onClick={() => setEditingAccessoryIdx(null)}
                              className="p-1 text-slate-400 hover:bg-slate-100 rounded cursor-pointer"
                              title="İptal"
                            >
                              <X className="w-4 h-4" />
                            </button>
                          </div>
                        );
                      }

                      return (
                        <div
                          key={idx}
                          className="group bg-white border border-blue-200 text-blue-950 text-xs font-semibold px-2.5 py-1.5 rounded-lg flex items-center space-x-1.5 shadow-sm hover:border-blue-300 transition-all"
                        >
                          <span>{acc}</span>
                          <button
                            type="button"
                            onClick={() => handleStartEditAccessory(idx, acc)}
                            title="Düzenle / İsmi Düzelt"
                            className="p-0.5 text-slate-400 hover:text-blue-600 rounded transition-colors cursor-pointer"
                          >
                            <Edit3 className="w-3.5 h-3.5" />
                          </button>
                          <button
                            type="button"
                            onClick={() => handleRemoveAccessory(acc)}
                            title="Listeden Kaldır"
                            className="p-0.5 text-slate-400 hover:text-rose-600 rounded transition-colors cursor-pointer"
                          >
                            <X className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div className="text-center py-2.5 text-xs text-slate-400 italic">
                    Henüz teslim alınan aksesuar eklenmedi.
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* SECTION 4: Customer Complaint & Issue Details */}
          <div className="space-y-3 pt-2">
            <div className="flex items-center space-x-2 text-slate-800 font-semibold text-sm border-b pb-2 border-slate-100">
              <FileText className="w-4 h-4 text-blue-600" />
              <span>4. Arıza Tanımı ve Müşteri Şikayeti</span>
            </div>

            <div>
              <label className="block text-xs font-semibold text-slate-700 mb-1">
                Müşteri Şikayeti Detayı <span className="text-rose-500">*</span>
              </label>
              <textarea
                required
                rows={3}
                placeholder="Arıza tanımı ve müşteri şikayeti..."
                value={issueDescription}
                onChange={e => setIssueDescription(e.target.value)}
                className="w-full px-3 py-2 text-sm border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
              />
            </div>
          </div>

          {/* Print options & Action Buttons */}
          <div className="pt-4 border-t border-slate-200 flex flex-col sm:flex-row items-center justify-between gap-3">
            <label className="flex items-center space-x-2 text-xs font-medium text-slate-700 cursor-pointer">
              <input
                type="checkbox"
                checked={printAfterSave}
                onChange={e => setPrintAfterSave(e.target.checked)}
                className="w-4 h-4 text-blue-600 rounded border-slate-300 focus:ring-blue-500"
              />
              <Printer className="w-4 h-4 text-slate-500" />
              <span>Kaydedince otomatik Müşteri Kabul Formu çıktısını aç</span>
            </label>

            <div className="flex flex-wrap items-center justify-end gap-2 w-full sm:w-auto">
              <button
                type="button"
                onClick={onClose}
                className="px-3.5 py-2 border border-slate-300 rounded-lg text-slate-700 hover:bg-slate-100 text-xs sm:text-sm font-medium transition-colors cursor-pointer"
              >
                İptal
              </button>

              <button
                type="submit"
                disabled={!!activeTicketInService || isSubmitting}
                className={`px-5 py-2 rounded-lg text-xs sm:text-sm font-bold flex items-center justify-center space-x-1.5 transition-all ${
                  activeTicketInService || isSubmitting
                    ? 'bg-slate-200 text-slate-500 border-2 border-slate-300 cursor-not-allowed opacity-90'
                    : 'bg-blue-600 hover:bg-blue-500 text-white shadow-md shadow-blue-600/30 hover:scale-[1.02] cursor-pointer'
                }`}
                title={
                  activeTicketInService
                    ? `Bu cihaz halihazırda servistedir (#${activeTicketInService.id}). Kayıt açılamaz.`
                    : isSubmitting
                    ? 'Kaydediliyor...'
                    : 'Servis kaydını oluştur'
                }
              >
                {activeTicketInService ? (
                  <>
                    <AlertCircle className="w-4 h-4 text-rose-600" />
                    <span>Cihaz Serviste (Kayıt Açılamaz)</span>
                  </>
                ) : isSubmitting ? (
                  <span>Kaydediliyor...</span>
                ) : (
                  <>
                    <Check className="w-4 h-4" />
                    <span>Servis Kaydını Tamamla</span>
                  </>
                )}
              </button>
            </div>
          </div>
        </form>

        {/* Edit Customer Modal */}
        <EditCustomerModal
          isOpen={isEditModalOpen}
          onClose={() => setIsEditModalOpen(false)}
          customer={editingCustomer}
          onSave={handleSaveEditedCustomer}
        />

        {/* Pattern Lock Modal */}
        <PatternLockModal
          isOpen={isPatternModalOpen}
          initialPattern={devicePassword}
          onClose={() => setIsPatternModalOpen(false)}
          onSave={(patternStr) => setDevicePassword(patternStr)}
        />

        {/* Quick New Contracted Company Modal */}
        {isQuickNewCompanyOpen && (
          <div className="fixed inset-0 z-60 bg-slate-900/60 backdrop-blur-xs flex items-center justify-center p-4">
            <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full border border-slate-200 overflow-hidden animate-in zoom-in-95">
              <div className="bg-purple-900 text-white p-4 flex items-center justify-between">
                <div className="flex items-center space-x-2">
                  <Building2 className="w-5 h-5 text-purple-300" />
                  <h3 className="font-bold text-sm">Hızlı Sözleşmeli Firma Ekle</h3>
                </div>
                <button
                  type="button"
                  onClick={() => setIsQuickNewCompanyOpen(false)}
                  className="text-purple-300 hover:text-white p-1 rounded-lg"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              <div className="p-5 space-y-4">
                <div>
                  <label className="block text-xs font-semibold text-slate-700 mb-1">
                    Firma Adı <span className="text-rose-500">*</span>
                  </label>
                  <input
                    type="text"
                    required
                    placeholder="Örn: ABC Lojistik A.Ş."
                    value={quickCompanyName}
                    onChange={e => setQuickCompanyName(e.target.value)}
                    className="w-full px-3 py-2 text-xs border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500/20 focus:border-purple-500"
                  />
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs font-semibold text-slate-700 mb-1">
                      Yetkili Kişi
                    </label>
                    <input
                      type="text"
                      placeholder="Örn: Mehmet Bey"
                      value={quickCompanyAuth}
                      onChange={e => setQuickCompanyAuth(e.target.value)}
                      className="w-full px-3 py-2 text-xs border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500/20"
                    />
                  </div>

                  <div>
                    <div className="flex items-center justify-between mb-1">
                      <label className="block text-xs font-semibold text-slate-700">
                        Telefon <span className="text-rose-500">*</span>
                      </label>
                      {quickCompanyPhone && (
                        <span className="text-[10px] font-mono text-purple-700 bg-purple-50 px-1 py-0.5 rounded">
                          {formatPhoneDisplay(quickCompanyPhone)}
                        </span>
                      )}
                    </div>
                    <input
                      type="tel"
                      inputMode="numeric"
                      pattern="[0-9]*"
                      maxLength={11}
                      required
                      placeholder="Firma Telefon Numarası"
                      value={quickCompanyPhone}
                      onChange={e => setQuickCompanyPhone(sanitizePhoneInput(e.target.value))}
                      className="w-full px-3 py-2 text-xs font-mono border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500/20"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-xs font-semibold text-slate-700 mb-1">
                    Aylık Anlaşma / Sözleşme Bedeli (₺) <span className="text-rose-500">*</span>
                  </label>
                  <div className="relative">
                    <input
                      type="number"
                      required
                      min="0"
                      step="1000"
                      value={quickCompanyFee}
                      onChange={e => setQuickCompanyFee(Number(e.target.value))}
                      className="w-full px-3 py-2 font-mono font-bold text-slate-900 border border-purple-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500/20 bg-purple-50/30 text-sm"
                    />
                    <span className="absolute right-3 top-2 text-xs font-bold text-purple-700">TL / Ay</span>
                  </div>
                  <p className="text-[11px] text-slate-500 mt-1">
                    💡 Bu tutar ön ödemeli bakiye olarak kaydedilecek, açılan servis kayıtlarının maliyetleri buradan düşülecektir.
                  </p>
                </div>

                <div className="pt-2 flex items-center justify-end space-x-2 border-t border-slate-100">
                  <button
                    type="button"
                    onClick={() => setIsQuickNewCompanyOpen(false)}
                    className="px-3 py-1.5 text-xs text-slate-600 hover:bg-slate-100 rounded-lg font-medium"
                  >
                    Vazgeç
                  </button>
                  <button
                    type="button"
                    onClick={handleQuickSaveCompany}
                    className="px-4 py-1.5 text-xs bg-purple-600 hover:bg-purple-700 text-white rounded-lg font-bold shadow-md shadow-purple-600/30"
                  >
                    Firmayı Kaydet & Seç
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
      </div>
    </div>
  );
};
